import { useId, useRef, useState } from 'react';
import { CloseRounded, ExpandMoreRounded } from '@mui/icons-material';
import { Accordion, AccordionDetails, AccordionSummary, Box, Button, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import { FormTextField, HelpPopover, StatusBadge, designTokens } from '../../../design-system';
import { candidateParts, evaluateMappingPreview, mappingCandidates, mappingSourceKeys } from '../model/mappings';
import type { ProfileMappingRule, SourcePropertyDefinition, TargetFieldDefinition } from '../model/types';

export function MappingRulePreview({ target, rule, sources, disabled, invalidReason }: {
  target: TargetFieldDefinition;
  rule: ProfileMappingRule;
  sources: SourcePropertyDefinition[];
  disabled: boolean;
  invalidReason?: string;
}) {
  const id = useId();
  const [overrides, setOverrides] = useState<Record<string, string>>({});
  const inputs = useRef(new Map<string, HTMLInputElement>());
  const candidates = mappingCandidates(rule);
  const sourceKeys = mappingSourceKeys(rule);
  const preview = evaluateMappingPreview(rule, sources, overrides);
  const usedCandidate = preview.candidateResults.find((candidate) => candidate.status === 'used');
  const changeExample = (key: string, value: string) => {
    if (!disabled) setOverrides((current) => ({ ...current, [key]: value }));
  };

  if (invalidReason) return <Stack spacing={1} component="section" aria-label={target.label + ' 결과 미리보기'}
    sx={{ p: designTokens.space.md + 'px', bgcolor: designTokens.color.neutralRaisedSurface, borderRadius: designTokens.radius.surface }}>
    <Typography component="h3" sx={designTokens.typography.subsectionTitle}>결과 미리보기</Typography>
    <Typography role="status" data-testid="mapping-preview-result">형식을 확인해 주세요</Typography>
    <Typography variant="body2" color="text.secondary">입력한 속성 이름을 수정하면 결과가 표시됩니다.</Typography>
  </Stack>;

  return <Stack component="section" aria-label={`${target.label} 결과 미리보기`} spacing={2}
    sx={{ minWidth: 0, p: `${designTokens.space.md}px`, bgcolor: designTokens.color.neutralRaisedSurface, borderRadius: designTokens.radius.surface }}>
    <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
      <Typography component="h3" sx={designTokens.typography.subsectionTitle}>결과 미리보기</Typography>
      <HelpPopover title="결과 미리보기">테스트용 예시입니다. 예시 값을 바꿔도 직원 정보나 동기화 설정에는 저장되지 않습니다.</HelpPopover>
      <StatusBadge label="예시" tone="neutral" />
    </Stack>
    <Box>
      <Typography data-testid="mapping-preview-result" role="status" aria-live="polite" aria-atomic="true"
        sx={{ ...designTokens.typography.metricValue, fontWeight: 700, overflowWrap: 'anywhere', whiteSpace: 'pre-wrap' }}>{preview.value || '사용할 값 없음'}</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
        {usedCandidate ? `${usedCandidate.index + 1}번째 값을 사용합니다.${usedCandidate.index > 0 ? ' 앞의 값은 사용 조건을 충족하지 않았습니다.' : ''}` : '사용 조건을 충족하는 값이 없습니다.'}
      </Typography>
      {!preview.value && <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
        {target.required ? '실제 직원에게도 값이 없으면 필수 정보를 만들 수 없어 해당 직원의 동기화가 실패합니다.' : '실제 직원에게도 값이 없으면 외부 프로필의 이 항목을 비웁니다.'}
      </Typography>}
    </Box>
    <Stack component="ol" spacing={1.5} sx={{ m: 0, p: 0, listStyle: 'none' }}>
      {preview.candidateResults.map((candidate) => <Box component="li" key={candidate.index} sx={{ p: `${designTokens.space.sm}px`, borderRadius: designTokens.radius.input, bgcolor: candidate.status === 'used' ? designTokens.color.canvas : undefined }}>
        <Stack direction="row" spacing={1} sx={{ alignItems: 'center', mb: 0.5 }}>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>{candidate.index + 1}번째 값</Typography>
          <StatusBadge label={candidate.status === 'used' ? '사용' : candidate.reason === 'earlier_candidate_used' ? '대기' : '건너뜀'} tone={candidate.status === 'used' ? 'success' : 'neutral'} />
        </Stack>
        <Typography variant="body2" sx={{ overflowWrap: 'anywhere' }}>{candidateParts(candidates[candidate.index]).map((part) => part.sourceProperty ? `[${sources.find((source) => source.key === part.sourceProperty)?.label ?? part.sourceProperty}]` : part.text).join('') || '연결된 속성 없음'}</Typography>
        <Typography variant="caption" color="text.secondary">
          {candidate.reason === 'required_source_empty' ? '일부 속성에 값이 없어 사용하지 않습니다.' : candidate.reason === 'earlier_candidate_used' ? '앞에서 사용할 값을 찾았습니다.' : candidate.status === 'used' ? '사용 조건을 충족했습니다.' : '값이 비어 있습니다.'}
        </Typography>
      </Box>)}
    </Stack>
    <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: 'wrap' }}>
      {candidates.length >= 2 && <Button variant="outlined" size="small" disabled={disabled} onClick={() => setOverrides((current) => ({ ...current, ...Object.fromEntries(candidateParts(candidates[0]).flatMap((part) => part.sourceProperty ? [[part.sourceProperty, '']] : [])) }))}>첫 번째 값 비워보기</Button>}
      <Button variant="text" size="small" disabled={disabled} onClick={() => setOverrides({})}>예시 초기화</Button>
    </Stack>
    <Accordion disabled={disabled || sourceKeys.length === 0}>
      <AccordionSummary id={`${id}-examples-trigger`} aria-controls={`${id}-examples-content`} expandIcon={<ExpandMoreRounded />}><Typography variant="body2">예시 값으로 테스트</Typography></AccordionSummary>
      <AccordionDetails id={`${id}-examples-content`}>
        <Stack spacing={2}>
          {sourceKeys.map((key) => {
            const source = sources.find((item) => item.key === key);
            const label = source?.label ?? key;
            const exampleValue = Object.hasOwn(overrides, key) ? overrides[key] : source?.example ?? '';
            return <Stack key={key} direction="row" spacing={1} sx={{ alignItems: 'flex-end' }}>
              <FormTextField fullWidth label={`${label} 예시 값`} value={exampleValue} disabled={disabled} onChange={(event) => changeExample(key, event.target.value)}
                inputRef={(element: HTMLInputElement | null) => { if (element) inputs.current.set(key, element); else inputs.current.delete(key); }} />
              <Tooltip title="예시 비우기"><span><IconButton aria-label={`${label} 예시 비우기`} disabled={disabled || !exampleValue} onClick={() => { changeExample(key, ''); inputs.current.get(key)?.focus(); }}><CloseRounded fontSize="small" /></IconButton></span></Tooltip>
            </Stack>;
          })}
        </Stack>
      </AccordionDetails>
    </Accordion>
  </Stack>;
}
